/** @format */
import { useTranslation } from 'next-i18next';
import { useContext, useMemo } from 'react';

import { SearchFacet } from '@toucan/common/services/search/constants';
import { FacetValue } from '@toucan/common/services/search/types';

import { toCamelCase } from '@toucan/client/global/axios';
import {
    centToDecimal,
    formatAsCurrency,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';

import {
    getStarRatingDisplayName,
    getTranslatedHumanisedFacetTitles,
} from '@toucan/client/core/search';

import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/config/types';
import {
    transformFacetsOptionValue,
    trimFilterOptions,
} from '@toucan/client/features/search/helpers';

import { ALLOWED_FACETS } from './constants';
import {
    FacetOptionTransformMap,
    UseProductFiltersProps,
    UseProductFiltersReturn,
} from './types';

export const useProductFilters = ({
    facets,
    filterQuery,
    selectedVariants,
    baseQuery,
    modifierGroups,
}: UseProductFiltersProps): UseProductFiltersReturn => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const translatedStarRatings = useMemo(() => {
        return getStarRatingDisplayName(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, []);

    const translatedFacetTitles = useMemo(() => {
        return getTranslatedHumanisedFacetTitles(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, []);

    const formatPrice = useMemo(() => {
        return formatAsCurrency(activeMarket.iso, activeMarket.currency);
    }, [activeMarket.iso, activeMarket.currency]);

    const facetLabelTransforms: FacetOptionTransformMap = useMemo(() => {
        return {
            productConditionStarRating: (value) => {
                // @ts-ignore - Element implicitly has an any type - Touch and Change
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Legacy code: do not use as an example.
                return translatedStarRatings[value];
            },
            productPrice: (value) => {
                value = transformFacetsOptionValue({
                    value,
                    t,
                });

                return value.replace(/\d+/g, (match: string) => {
                    return formatPrice(centToDecimal(parseInt(match)), {
                        trailingZeros: false,
                    });
                });
            },
            modelYearOfReleaseModelSpecificationNum: (value) => {
                const beforeLanguage = (value: string) =>
                    t('Before {{value}}', {
                        value,
                        context: 'search-filters',
                    });
                const afterLanguage = (value: string) =>
                    t('After {{value}}', {
                        value,
                        context: 'search-filters',
                    });

                return transformFacetsOptionValue({
                    value,
                    bottomBoundaryLanguage: beforeLanguage,
                    topBoundaryLanguage: afterLanguage,
                    t,
                });
            },
            productPriceModifierIds: (value) => {
                const modifiers = [
                    ...(modifierGroups?.essential ?? []),
                    ...(modifierGroups?.extra ?? []),
                ]?.flatMap(({ modifiers }) => modifiers);

                const modifier = modifiers?.find(
                    (modifier) => `${modifier.id}` === value
                );

                return modifier?.name ?? '';
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [t, modifierGroups, formatPrice]);

    function getFilterOptionLabel(value: string, facetId: SearchFacet) {
        const transformer = facetLabelTransforms[facetId];

        if (transformer) {
            return transformer(value);
        }

        if (value === 'true') {
            return t('Yes');
        }

        if (value === 'false') {
            return t('No');
        }

        return null;
    }

    function transformFilterQueryToOptions(
        facetId: SearchFacet
    ): FilterOption[] {
        if (!filterQuery?.[facetId]) {
            return [];
        }
        const filter = filterQuery[facetId];
        const options = Array.isArray(filter) ? filter : [filter];

        return options.map((option) => {
            const value = option.toString();
            return {
                label: getFilterOptionLabel(value, facetId) ?? value,
                value,
            };
        });
    }

    function transformFacetsToFilters(): Filter[] {
        return facets
            .filter((facet) => {
                const id = toCamelCase<SearchFacet>(facet.field);

                if (!ALLOWED_FACETS.includes(id)) return false;

                const countFacetsValues = facet.values.filter(
                    (value: FacetValue) => parseInt(value.count) > 0
                ).length;

                const isPriceFilter = id === SearchFacet.productPrice;

                if (isPriceFilter) return countFacetsValues >= 1;

                return countFacetsValues >= 2;
            })
            .map((facet): Filter => {
                const id = toCamelCase<SearchFacet>(facet.field);
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
                const label = translatedFacetTitles?.[id] || id;

                const isPriceFilter = id === SearchFacet.productPrice;

                let options: FilterOption[] = facet.values
                    .filter(
                        (value) => isPriceFilter || parseInt(value.count) > 0
                    )
                    .map((option) => {
                        const quantity = parseInt(option.count);
                        const label = getFilterOptionLabel(
                            option.fieldValue,
                            id
                        );

                        return {
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
                            label: label || option.fieldValue,
                            value: option.fieldValue,
                            quantity,
                        };
                    });

                if (isPriceFilter) {
                    options = trimFilterOptions(options);
                }

                return {
                    id,
                    label,
                    options,
                    selectedOptions: transformFilterQueryToOptions(id),
                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                    selectedVariant: selectedVariants[id],
                    displayOptionQuantity: false,
                    baseFilterQuery: baseQuery.filterQuery!,
                    getAllOptions: () => Promise.resolve([]),
                };
            });
    }

    const filters = useMemo(() => {
        return transformFacetsToFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [facets, filterQuery, modifierGroups]);

    const filtersAppliedCount = filters.reduce((count, filter) => {
        return count + filter.selectedOptions.length;
    }, 0);

    return { filters, filtersAppliedCount };
};
